import { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Alert, Form, InputGroup } from "react-bootstrap";
import { Plus, Trash } from "react-bootstrap-icons";
import { Input } from "../../Controls/Input";
import { Select } from "../../Controls/Select";
import { MoneyInput } from "../../Controls/MoneyInput";
import { createApiService } from "../../shared/apiConfig";
import { 
    BillingApi, 
    ServerRequestsBillingPlanDetailDto, 
    ServerRequestsBillingRuleDto,
    DataEntitiesFinanceBillingRuleType
} from "../../shared/api/axios-client";
import { showMoney } from "../../Data/Money";

interface BillingPlanEditModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    plan: ServerRequestsBillingPlanDetailDto | null;
    onSaved: () => void;
    isCloneMode: boolean;
    operatorId: number;
}

interface BillingRuleForm {
    billingRuleId?: number;
    name: string;
    ruleType: DataEntitiesFinanceBillingRuleType;
    amount: number;
    customProcedureName?: string;
    paymentType: 'customer' | 'spotsync';
}

export function BillingPlanEditModal({ show, setShow, plan, onSaved, isCloneMode, operatorId }: BillingPlanEditModalProps) {
    const [planName, setPlanName] = useState("");
    const [planDescription, setPlanDescription] = useState("");
    const [billingRules, setBillingRules] = useState<BillingRuleForm[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasAssignedFacilities, setHasAssignedFacilities] = useState(false);

    const billingService = createApiService(BillingApi);

    const ruleTypeOptions = [
        { value: 0, label: "Per Transaction", description: "Calculate based on # of transactions in the given timeframe" },
        /* { value: 1, label: "% Revenue Based", description: "Calculate based on percentage of revenue accumulated" }, */
        { value: 2, label: "Flat Rate", description: "A flat amount that will be charged each cycle" },
        { value: 4, label: "Park Guard Per Transaction", description: "Calculate based on # of parkguard enrollments" },
         /* { value: 3, label: "Custom Procedure", description: "Custom calculation procedure" } */
    ];

    const getRuleTypeLabel = (type: number) => {
        const option = ruleTypeOptions.find(opt => opt.value === type);
        return option?.label || "Unknown";
    };

    const getRuleTypeDescription = (type: number) => {
        const option = ruleTypeOptions.find(opt => opt.value === type);
        return option?.description || "";
    };

    const isPercentageType = (ruleType: number) => ruleType === 1; // % Revenue Based

    useEffect(() => {
        if (show) {
            if (plan && !isCloneMode) {
                // Editing existing plan
                setPlanName(plan.name || "");
                setPlanDescription(plan.description || "");
                setBillingRules(plan.billingRules?.map(rule => {
                    const isNegative = rule.amount < 0;
                    const absoluteAmount = Math.abs(rule.amount);
                    return {
                        billingRuleId: rule.billingRuleId,
                        name: rule.name || "",
                        ruleType: rule.ruleType,
                        amount: rule.ruleType === 1 ? absoluteAmount : absoluteAmount / 100, // Only convert cents to dollars for non-percentage types
                        customProcedureName: rule.customProcedureName || "",
                        paymentType: isNegative ? 'spotsync' : 'customer'
                    };
                }) || []);
                setHasAssignedFacilities((plan.facilityAssignments?.length || 0) > 0);
            } else if (plan && isCloneMode) {
                // Cloning existing plan
                setPlanName(`${plan.name || "Untitled Plan"} (Copy)`);
                setPlanDescription(plan.description || "");
                setBillingRules(plan.billingRules?.map(rule => {
                    const isNegative = rule.amount < 0;
                    const absoluteAmount = Math.abs(rule.amount);
                    return {
                        name: rule.name || "",
                        ruleType: rule.ruleType,
                        amount: rule.ruleType === 1 ? absoluteAmount : absoluteAmount / 100, // Only convert cents to dollars for non-percentage types
                        customProcedureName: rule.customProcedureName || "",
                        paymentType: isNegative ? 'spotsync' : 'customer'
                    };
                }) || []);
                setHasAssignedFacilities(false);
            } else {
                // Creating new plan
                setPlanName("");
                setPlanDescription("");
                setBillingRules([]);
                setHasAssignedFacilities(false);
            }
            setError("");
        }
    }, [show, plan, isCloneMode]);

    const addBillingRule = () => {
        setBillingRules([...billingRules, {
            name: "",
            ruleType: 0, // Per Transaction
            amount: 0,
            customProcedureName: "",
            paymentType: 'customer'
        }]);
    };

    const updateBillingRule = (index: number, field: keyof BillingRuleForm, value: any) => {
        const updatedRules = [...billingRules];
        updatedRules[index] = { ...updatedRules[index], [field]: value };
        setBillingRules(updatedRules);
    };

    const removeBillingRule = (index: number) => {
        setBillingRules(billingRules.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setIsLoading(true);
        setError("");

        try {
            // Validate form
            if (!planName.trim()) {
                setError("Plan name is required");
                return;
            }

            if (billingRules.length === 0) {
                setError("At least one billing rule is required");
                return;
            }

            // Validate billing rules
            for (const rule of billingRules) {
                if (!rule.name.trim()) {
                    setError("All billing rules must have a name");
                    return;
                }
                if (rule.ruleType === 3 && !rule.customProcedureName?.trim()) {
                    setError("Custom procedure name is required for custom rules");
                    return;
                }
                if (rule.ruleType === 1 && (rule.amount < 0 || rule.amount > 1)) {
                    setError("Percentage amounts must be between 0% and 100%");
                    return;
                }
            }

            if (plan && !isCloneMode) {
                // Update existing plan
                await billingService.updateBillingPlan({
                    id: plan.billingPlanId!,
                    serverRequestsUpdateBillingPlanRequest: {
                        name: planName,
                        description: planDescription,
                        billingRules: billingRules.map(rule => {
                            const baseAmount = rule.ruleType === 1 ? rule.amount : Math.round(rule.amount * 100);
                            const finalAmount = rule.paymentType === 'spotsync' ? -baseAmount : baseAmount;
                            return {
                                billingRuleId: rule.billingRuleId,
                                name: rule.name,
                                ruleType: rule.ruleType,
                                amount: finalAmount,
                                customProcedureName: rule.customProcedureName || null
                            };
                        })
                    }
                });
            } else {
                // Create new plan
                await billingService.createBillingPlan({
                    serverRequestsCreateBillingPlanRequest: {
                        name: planName,
                        description: planDescription,
                        billingRules: billingRules.map(rule => {
                            const baseAmount = rule.ruleType === 1 ? rule.amount : Math.round(rule.amount * 100);
                            const finalAmount = rule.paymentType === 'spotsync' ? -baseAmount : baseAmount;
                            return {
                                name: rule.name,
                                ruleType: rule.ruleType,
                                amount: finalAmount,
                                customProcedureName: rule.customProcedureName || null
                            };
                        })
                    }
                });
            }

            onSaved();
            setShow(false);
        } catch (error: any) {
            console.error("Error saving billing plan:", error);
            setError(error.response?.data?.message || "Failed to save billing plan");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setShow(false);
    };

    const title = plan && !isCloneMode ? "Edit Billing Plan" : "Create Billing Plan";

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {hasAssignedFacilities && !isCloneMode && (
                    <Alert variant="warning" className="mb-3">
                        <strong>Warning:</strong> This billing plan is currently assigned to facilities. 
                        Changes will affect billing for those facilities.
                    </Alert>
                )}
                
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                <Row className="mb-3">
                    <Col md={6}>
                        <Input
                            type="text"
                            label="Plan Name"
                            value={planName}
                            onChange={(e) => setPlanName(e.target.value)}
                            placeholder="Enter plan name"
                            required
                        />
                    </Col>
                    <Col md={6}>
                        <Input
                            type="text"
                            label="Description"
                            value={planDescription}
                            onChange={(e) => setPlanDescription(e.target.value)}
                            placeholder="Enter plan description"
                        />
                    </Col>
                </Row>

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Billing Rules</h5>
                    <Button variant="outline-primary" size="sm" onClick={addBillingRule}>
                        <Plus size={16} className="me-1" />
                        Add Rule
                    </Button>
                </div>

                {billingRules.length === 0 && (
                    <Alert variant="info">
                        No billing rules defined. Click "Add Rule" to create your first billing rule.
                    </Alert>
                )}

                {billingRules.map((rule, index) => (
                    <div key={index} className="border rounded p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Rule #{index + 1}</h6>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeBillingRule(index)}
                            >
                                <Trash size={16} />
                            </Button>
                        </div>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Input
                                    type="text"
                                    label="Rule Name"
                                    value={rule.name}
                                    onChange={(e) => updateBillingRule(index, 'name', e.target.value)}
                                    placeholder="This will appear as the line item in the invoice"
                                    required
                                />
                            </Col>
                            <Col md={6}>
                                <Select
                                    label="Rule Type"
                                    value={rule.ruleType}
                                    onChange={(e) => updateBillingRule(index, 'ruleType', parseInt(e.target.value))}
                                    required
                                >
                                    {ruleTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Select
                                    label="Payment Type"
                                    value={rule.paymentType}
                                    onChange={(e) => updateBillingRule(index, 'paymentType', e.target.value)}
                                    required
                                >
                                    <option value="customer">Customer pays</option>
                                    <option value="spotsync">SpotSync pays Customer</option>
                                </Select>
                            </Col>
                            <Col md={6}>
                                {isPercentageType(rule.ruleType) ? (
                                    <div>
                                        <Form.Label>Percentage</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control
                                                type="number"
                                                value={rule.amount * 100}
                                                onChange={(e) => updateBillingRule(index, 'amount', parseFloat(e.target.value) / 100)}
                                                placeholder="0.0"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </div>
                                ) : (
                                    <div>
                                        <Form.Label>Amount</Form.Label>
                                        <InputGroup className="mb-3">
                                            <InputGroup.Text>$</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                value={rule.amount}
                                                onChange={(e) => updateBillingRule(index, 'amount', parseFloat(e.target.value) || 0)}
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                            />
                                        </InputGroup>
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <div className="text-muted small">
                            <strong>{getRuleTypeLabel(rule.ruleType)}:</strong> {getRuleTypeDescription(rule.ruleType)}
                        </div>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? "Saving..." : "Save Plan"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}